import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Home as HomeIcon, GitCompare } from "lucide-react";
import Header from "@/components/Header";
import QuoteBanner from "@/components/QuoteBanner";
import ConstituencySearch from "@/components/ConstituencySearch";
import CandidateCard from "@/components/CandidateCard";
import PartyChip from "@/components/PartyChip";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LangContext";
import { fetchCandidates } from "@/lib/api";
import { fetchSeat } from "@/lib/insights";

export default function Results() {
  const { name } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [seat, setSeat] = useState(null);

  useEffect(() => {
    setSeat(null);
    fetchSeat(decodeURIComponent(name)).then(setSeat).catch(() => setSeat(null));
  }, [name]);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    fetchCandidates(decodeURIComponent(name))
      .then(setData)
      .catch((e) => setErr(e?.message || "Error"))
      .finally(() => setLoading(false));
  }, [name]);

  function handleSelect(item) {
    navigate(`/c/${encodeURIComponent(item.english)}`);
  }

  const candidates = useMemo(() => data?.items || [], [data]);
  const winner = candidates.find((c) => c.is_winner);
  const defeated = candidates.filter((c) => !c.is_winner);
  const displayConstituency =
    lang === "ta" && data?.constituency_ta ? data.constituency_ta : data?.constituency_en || decodeURIComponent(name);

  // Distinct parties for preview row
  const parties = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of candidates) {
      const k = c.party_short || c.party_en;
      if (!seen.has(k)) {
        seen.add(k);
        out.push({
          short: c.party_short,
          label: lang === "ta" ? (c.party_ta || c.party_short) : c.party_short,
        });
      }
    }
    return out;
  }, [candidates, lang]);

  return (
    <div className="App">
      <Header compact title={t("nav_candidates")} />
      <QuoteBanner />

      <main className="max-w-4xl mx-auto px-6 pt-6 pb-24">

        <button
          onClick={() => navigate("/")}
          className={`mt-6 mb-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-[#1a1640] shadow-sm hover:bg-[#f3eedc] active:scale-[0.99] transition ${
            lang === "ta" ? "font-tamil" : ""
          }`}
          data-testid="home-btn"
        >
          <HomeIcon size={16} strokeWidth={2.4} />
          {t("home_btn")}
        </button>

        {/* Keep search bar (per requirement: "Search Your Constituency" text continues to display) */}
        <ConstituencySearch onSelect={handleSelect} initialValue={displayConstituency} />

        {/* Constituency title */}
        <section className="mt-8" data-testid="constituency-summary">
          <h2
            className={`text-3xl sm:text-4xl font-semibold text-[#1a1640] tracking-tight ${
              lang === "ta" ? "font-tamil" : "font-display"
            }`}
            data-testid="constituency-title"
          >
            {displayConstituency}
          </h2>
          {!loading && !err && (
            <p
              className={`mt-2 text-[#6b6894] text-[15px] ${
                lang === "ta" ? "font-tamil" : ""
              }`}
              data-testid="constituency-meta"
            >
              {candidates.length} {candidates.length === 1 && lang === "en" ? "Candidate" : t("candidates")}
            </p>
          )}

          {parties.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="party-preview-row">
              {parties.map((p) => (
                <PartyChip key={p.short} short={p.short} label={p.label} size="lg" />
              ))}
            </div>
          )}

          {candidates.length > 0 && (
            <p
              className={`mt-5 text-[13px] text-[#8c89ad] ${
                lang === "ta" ? "font-tamil" : ""
              }`}
              data-testid="tap-card-hint"
            >
              {t("tap_card_hint")}
            </p>
          )}
        </section>

        {seat && (
          <Link to={`/changes?seat=${seat.ac_no}`} className={`mt-5 inline-flex items-center gap-2 rounded-full bg-[#1a1640] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#2a2170] transition-colors ${lang === "ta" ? "font-tamil" : ""}`} data-testid="results-changes-link">
            <GitCompare size={14} /> {t("see_changes_link")}
          </Link>
        )}

        {/* Candidates */}
        <section className="mt-5 space-y-4" data-testid="candidate-list">
          {loading && (
            <div className="bg-white rounded-[22px] p-6 animate-pulse">
              <div className="h-5 bg-slate-200 rounded w-1/3" />
              <div className="mt-3 h-4 bg-slate-100 rounded w-1/2" />
            </div>
          )}

          {!loading && candidates.length === 0 && (
            <div
              className="bg-white rounded-[22px] p-8 text-center"
              data-testid="no-candidates"
            >
              <div className="text-5xl">🪧</div>
              <h3
                className={`mt-3 text-xl font-semibold text-[#1a1640] ${
                  lang === "ta" ? "font-tamil" : "font-display"
                }`}
              >
                {t("no_candidates_title")}
              </h3>
              <p
                className={`mt-2 text-[#6b6894] ${lang === "ta" ? "font-tamil" : ""}`}
              >
                {t("no_candidates_sub")}
              </p>
              <button
                onClick={() => navigate("/")}
                className={`mt-5 text-sm font-semibold text-indigo-700 hover:text-indigo-900 ${
                  lang === "ta" ? "font-tamil" : ""
                }`}
                data-testid="back-home-btn"
              >
                {t("back_home")}
              </button>
            </div>
          )}

          {!loading && winner && <CandidateCard key={`w-${winner.sno}`} c={winner} idx={0} winner />}
          {!loading && candidates.length > 0 && !winner && (
            <p className={`text-[13px] text-[#8c89ad] ${lang === "ta" ? "font-tamil" : ""}`} data-testid="no-winner-match">{t("no_winner_match")}</p>
          )}
          {!loading && defeated.length > 0 && (
            <div className="pt-4" data-testid="defeated-section">
              <h3 className={`text-xl font-semibold text-[#1a1640] ${lang === "ta" ? "font-tamil" : "font-display"}`} data-testid="defeated-title">
                {(winner ? t("defeated_title") : t("all_candidates_title")).replace("{n}", defeated.length)}
              </h3>
              {winner && <p className={`mt-1 text-[12px] text-[#8c89ad] ${lang === "ta" ? "font-tamil" : ""}`}>{t("defeated_note")}</p>}
            </div>
          )}
          {!loading &&
            defeated.map((c, i) => (
              <CandidateCard key={`${c.sno}-${c.name}`} c={c} idx={i + 1} />
            ))}
        </section>
      </main>
      <Footer />
    </div>
  );
}
